/**
 * Every candidate Cora screened in the period, newest first, filterable the way TA searches: by
 * name / position / recruiter, by verdict and by status. A row opens the full run.
 */
import { useMemo, useState } from 'react';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@finn-auto/design-system/atoms/select';
import { Button } from '@finn-auto/design-system/atoms/button';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import type { CoraMode, CoraRunSummary, RunStatus } from '../../services/coraApi';
import ScreenCandidateForm from './ScreenCandidateForm';
import {
  OUTCOME_LABEL,
  RECOMMENDATIONS,
  RECOMMENDATION_CLASS,
  RECOMMENDATION_LABEL,
  STATUS_LABEL,
  fmtWhen,
  matchesQuery,
} from './coraFormat';

const ALL = 'all';
const PAGE = 100;

export function RecommendationChip({ value }: { value: CoraRunSummary['recommendation'] }) {
  if (!value) return <span className="text-zinc-500">—</span>;
  return (
    <span className={`inline-block whitespace-nowrap rounded-sm border border-solid px-2 py-0.5 text-xs font-medium ${RECOMMENDATION_CLASS[value]}`}>
      {RECOMMENDATION_LABEL[value]}
    </span>
  );
}

/** `mode` is null until Cora's status has loaded: no screening can be started on a guessed mode. */
export default function ScreeningsTab({ runs, mode, onOpen }: { runs: CoraRunSummary[]; mode: CoraMode | null; onOpen: (id: string) => void }) {
  const [query, setQuery] = useState('');
  const [recommendation, setRecommendation] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [shown, setShown] = useState(PAGE);

  const filtered = useMemo(
    () =>
      runs.filter(
        (r) =>
          matchesQuery(r, query) &&
          (recommendation === ALL || r.recommendation === recommendation) &&
          (status === ALL || r.status === status)
      ),
    [runs, query, recommendation, status]
  );

  const filterChanged = (filter: string, value: string) => {
    setShown(PAGE);
    void logAnalyticsEvent('cora_filter_changed', { filter, value });
  };

  return (
    <div className="flex flex-col gap-4">
      {mode && <ScreenCandidateForm mode={mode} onScreened={onOpen} />}
      <div className="grid gap-3 md:grid-cols-[1fr_14rem_14rem]">
        <Input
          label="Search candidate, position or recruiter"
          value={query}
          onChange={(e) => {
            setQuery((e.target as HTMLInputElement).value);
            setShown(PAGE);
          }}
        />
        <Select value={recommendation} onValueChange={(v: string) => { setRecommendation(v); filterChanged('recommendation', v); }}>
          <SelectTrigger aria-label="Verdict"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All verdicts</SelectItem>
            {RECOMMENDATIONS.map((r) => <SelectItem key={r} value={r}>{RECOMMENDATION_LABEL[r]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v: string) => { setStatus(v); filterChanged('status', v); }}>
          <SelectTrigger aria-label="Status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            {(Object.keys(STATUS_LABEL) as RunStatus[]).map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <p className="text-sm text-zinc-600">{filtered.length} of {runs.length} screenings</p>

      {filtered.length === 0 ? (
        <p className="rounded-sm border border-solid border-pearl p-6 text-center text-sm text-zinc-600">
          No screenings match. Cora screens new applicants every hour on the positions ticked &quot;Screened by Cora&quot; in Airtable.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-sm border border-solid border-pearl">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-solid border-pearl text-xs uppercase text-zinc-500">
              <tr>
                <th className="px-3 py-2">Candidate</th>
                <th className="px-3 py-2">Position</th>
                <th className="px-3 py-2">Verdict</th>
                <th className="px-3 py-2">Salary</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Next step</th>
                <th className="px-3 py-2">Screened</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, shown).map((r) => (
                <tr
                  key={r.id}
                  tabIndex={0}
                  className="cursor-pointer border-b border-solid border-pearl last:border-0 hover:bg-pearl/40 focus:bg-pearl/40"
                  onClick={() => onOpen(r.id)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen(r.id)}
                >
                  <td className="px-3 py-2 font-medium text-zinc-900">{r.candidateName || '—'}</td>
                  <td className="px-3 py-2 text-zinc-700">{r.positionTitle}<span className="block text-xs text-zinc-500">{r.recruiter}</span></td>
                  <td className="px-3 py-2"><RecommendationChip value={r.recommendation} /></td>
                  <td className="px-3 py-2">{r.salaryMismatch ? <span className="text-orange">Mismatch</span> : '—'}</td>
                  <td className={`px-3 py-2 ${r.status === 'failed' || r.status === 'write_failed' ? 'text-red' : 'text-zinc-700'}`}>
                    {STATUS_LABEL[r.status]}
                  </td>
                  <td className="px-3 py-2 text-zinc-700">{OUTCOME_LABEL[r.outcome]}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-zinc-600">{fmtWhen(r.finishedAt || r.startedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {filtered.length > shown && (
        <div>
          <Button variant="secondary" onClick={() => setShown((n) => n + PAGE)}>Show {Math.min(PAGE, filtered.length - shown)} more</Button>
        </div>
      )}
    </div>
  );
}
